import type {Component} from 'grapesjs';
import type {BirthdayFormat, FieldMeta} from '@FormMakerReact/form-maker.types';
import {POSTERMYWALL_EMAIL_BOX_TYPE} from '@FormMakerReact/components/postermywall-email-box/postermywall-email-box';
import {POSTERMYWALL_FIRST_NAME_BOX_TYPE} from '@FormMakerReact/components/postermywall-first-name-box/postermywall-first-name-box';
import {POSTERMYWALL_LAST_NAME_BOX_TYPE} from '@FormMakerReact/components/postermywall-last-name-box/postermywall-last-name-box';
import {POSTERMYWALL_BIRTHDAY_BOX_TYPE} from '@FormMakerReact/components/postermywall-birthday-box/postermywall-birthday-box';
import {POSTERMYWALL_ADDRESS_BOX_TYPE} from '@FormMakerReact/components/postermywall-address-box/postermywall-address-box';
import {POSTERMYWALL_PHONE_BOX_TYPE} from '@FormMakerReact/components/postermywall-phone-box/postermywall-phone-box';
import type {FieldEditRowKind} from '@FormMakerReact/layout/form-maker-right-panel/controls/field-edit-row/field-edit-row.types';

export interface FieldToggleListControlProps {
  component: Component;
  hideSectionHeading?: boolean;
}

export interface FieldRowConfig {
  boxType: string;
  /** i18n key for standard fields; empty for custom fields (label comes from fieldsMeta). */
  labelKey: string;
  iconClass: string;
  editKind: FieldEditRowKind;
  /** False for non-expandable rows (Email) — they render only the locked toggle. */
  isExpandable: boolean;
  /** Locked rows (Email) are always-on and cannot be toggled off. */
  isLocked: boolean;
  /** Present and > 0 for custom-box rows; undefined for all standard rows. */
  customFieldIndex?: number;
}

export const STANDARD_FIELD_ROW_CONFIGS: readonly FieldRowConfig[] = [
  {boxType: POSTERMYWALL_EMAIL_BOX_TYPE, labelKey: 'pmwjs_email', iconClass: 'icon-at', editKind: 'standard', isExpandable: false, isLocked: true},
  {boxType: POSTERMYWALL_FIRST_NAME_BOX_TYPE, labelKey: 'pmwjs_first_name', iconClass: 'icon-user', editKind: 'standard', isExpandable: true, isLocked: false},
  {boxType: POSTERMYWALL_LAST_NAME_BOX_TYPE, labelKey: 'pmwjs_last_name', iconClass: 'icon-user', editKind: 'standard', isExpandable: true, isLocked: false},
  {boxType: POSTERMYWALL_BIRTHDAY_BOX_TYPE, labelKey: 'pmwjs_birthday', iconClass: 'icon-calendar', editKind: 'birthday', isExpandable: true, isLocked: false},
  {boxType: POSTERMYWALL_ADDRESS_BOX_TYPE, labelKey: 'pmwjs_address', iconClass: 'icon-location', editKind: 'standard', isExpandable: true, isLocked: false},
  {boxType: POSTERMYWALL_PHONE_BOX_TYPE, labelKey: 'pmwjs_phone', iconClass: 'icon-phone', editKind: 'standard', isExpandable: true, isLocked: false},
];

export const STANDARD_BOX_TYPES: ReadonlySet<string> = new Set<string>(STANDARD_FIELD_ROW_CONFIGS.map((c) => c.boxType));

export const STANDARD_CONFIG_BY_BOX_TYPE: ReadonlyMap<string, FieldRowConfig> = new Map<string, FieldRowConfig>(STANDARD_FIELD_ROW_CONFIGS.map((c) => [c.boxType, c] as const));

export interface FieldToggleRowProps {
  boxType: string;
  label: string;
  iconClass: string;
  /** Forced true for locked rows (Email) regardless of canvas state. */
  isEnabled: boolean;
  isLocked: boolean;
  isExpandable: boolean;
  isEditOpen: boolean;
  editKind: FieldEditRowKind;
  editLabelValue: string;
  editBirthdayFormat: BirthdayFormat;
  isRequired: boolean;
  onToggle: () => void;
  onEditToggle: () => void;
  onLabelChange: (newLabel: string) => void;
  onFormatChange: (newFormat: BirthdayFormat) => void;
  onRequiredChange: (isRequired: boolean) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /** True when this row is the first reorderable slot — disables move-up. */
  isFirstMovable: boolean;
  /** True when this row is the last reorderable slot — disables move-down. */
  isLastMovable: boolean;
}

export interface FieldRowRenderArgs {
  config: FieldRowConfig;
  rowIndex: number;
  totalRows: number;
  enabledBoxTypes: ReadonlySet<string>;
  onToggle: (boxType: string) => void;
  onToggleCustomField: (customFieldIndex: number) => void;
  onEditToggle: (boxType: string) => void;
  onLabelChange: (boxType: string, newLabel: string) => void;
  onCustomLabelChange: (customFieldIndex: number, newLabel: string) => void;
  onFormatChange: (newFormat: BirthdayFormat) => void;
  onRequiredChange: (boxType: string, customFieldIndex: number | undefined, isRequired: boolean) => void;
  reorderRows: (fromIndex: number, toIndex: number) => void;
  openEditBoxTypes: ReadonlySet<string>;
  currentBirthdayFormat: BirthdayFormat;
  getLabelForConfig: (config: FieldRowConfig) => string;
  getIsRequiredForConfig: (config: FieldRowConfig) => boolean;
  fieldsMeta: FieldMeta[];
}
